import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock, Clock, Laptop } from "lucide-react";

import { AppShell } from "@/components/app/app-shell";
import { LogHoursDialog } from "@/components/app/log-hours-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { timeSummaryQuery } from "@/lib/queries";
import { sectionLabel } from "@/lib/time-sections";

export const Route = createFileRoute("/_authenticated/time")({
  head: () => ({
    meta: [
      { title: "Time Tracking | Warrior Volunteer" },
      {
        name: "description",
        content: "Volunteer hours from events and time worked inside the T1D Warriors app.",
      },
      { property: "og:title", content: "Time Tracking | Warrior Volunteer" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      {
        property: "og:description",
        content: "Volunteer hours from events and time worked inside the T1D Warriors app.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: TimePage,
});

const SOURCE_LABEL: Record<string, string> = {
  event: "Event shift",
  app: "In app",
  manual: "Logged by hand",
};

function formatHours(value: number) {
  return `${Math.round(value * 100) / 100} hr`;
}

function formatWhen(value: string | null) {
  if (!value) return "-";
  return new Date(value).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function TimePage() {
  const { data } = useQuery(timeSummaryQuery);

  const totals = data?.totals ?? { event: 0, app: 0, manual: 0, all: 0 };

  return (
    <AppShell
      eyebrow="Impact"
      title="Time tracking"
    >
      <div className="panel mb-4 flex flex-col items-start justify-between gap-3 p-5 sm:flex-row sm:items-center">
        <div>
          <p className="font-semibold text-ice">Worked outside the app?</p>
          <p className="t-meta mt-0.5">Log your hours here so they count toward your totals.</p>
        </div>
        <LogHoursDialog />
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Clock} label="All hours" value={formatHours(totals.all)} highlight />
        <StatCard icon={CalendarClock} label="Event shifts" value={formatHours(totals.event)} />
        <StatCard icon={Laptop} label="In the app" value={formatHours(totals.app)} />
        <StatCard icon={Clock} label="Logged by hand" value={formatHours(totals.manual)} />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[360px_1fr]">
        <section className="panel h-fit p-6">
          <h2 className="t-h3 text-ice">Where app time goes</h2>
          <p className="t-meta mt-1">
            Time counts only while you are active in a tab. It pauses after five idle minutes.
          </p>
          <div className="mt-4 space-y-3">
            {(data?.sections ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">Nothing tracked yet.</p>
            ) : null}
            {(data?.sections ?? []).map((row) => (
              <div key={row.section} className="flex items-center justify-between gap-4">
                <span className="text-sm text-ice">{sectionLabel(row.section)}</span>
                <span className="tabular-nums text-sm font-semibold text-cyan">
                  {formatHours(row.hours)}
                </span>
              </div>
            ))}
          </div>
        </section>

        <div className="space-y-6">
          <section className="panel overflow-x-auto">
            <div className="px-5 pt-5">
              <h2 className="t-h3 text-ice">Recent hours</h2>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>What</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead className="text-right">Hours</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.recent ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="py-10 text-center text-muted-foreground">
                      Nothing tracked yet.
                    </TableCell>
                  </TableRow>
                ) : null}
                {(data?.recent ?? []).map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="t-meta">{row.activity_date}</TableCell>
                    <TableCell>
                      <p className="text-ice">{row.activity_type}</p>
                      {row.organizations?.name ? <p className="t-meta mt-1">{row.organizations.name}</p> : null}
                      {row.notes ? <p className="t-meta mt-1">{row.notes}</p> : null}
                    </TableCell>
                    <TableCell className="t-meta">
                      {SOURCE_LABEL[row.source ?? "manual"] ?? "Logged by hand"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">
                      {row.hours}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </section>

          <section className="panel overflow-x-auto">
            <div className="px-5 pt-5">
              <h2 className="t-h3 text-ice">App sessions</h2>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Started</TableHead>
                  <TableHead>Ended</TableHead>
                  <TableHead className="text-right">Active time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.sessions ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="py-10 text-center text-muted-foreground">
                      No sessions recorded yet.
                    </TableCell>
                  </TableRow>
                ) : null}
                {(data?.sessions ?? []).map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="t-meta">{formatWhen(row.started_at)}</TableCell>
                    <TableCell className="t-meta">
                      {row.ended_at ? formatWhen(row.ended_at) : "In progress"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">
                      {formatHours((row.active_seconds ?? 0) / 3600)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </section>
        </div>
      </div>
    </AppShell>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  highlight,
}: {
  icon: typeof Clock;
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="panel p-5">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="h-4 w-4" />
        <p className="t-meta">{label}</p>
      </div>
      <p
        className={`mt-2 text-2xl font-bold tabular-nums ${highlight ? "text-cyan" : "text-ice"}`}
      >
        {value}
      </p>
    </div>
  );
}
